import React, { useState, useEffect, useRef } from 'react';

import { districts } from 'srilanka-districts-map';
import SectionCard from './components/SectionCard';
import './demo.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import SriLankaDistrictMap from 'srilanka-districts-map';

interface TooltipState {
  visible: boolean;
  content: string;
  x: number;
  y: number;
}

function App() {
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [highlightColor, setHighlightColor] = useState<string>('#4361ee');
  const [defaultColor, setDefaultColor] = useState<string>('#e8f0fe');
  const [strokeColor, setStrokeColor] = useState<string>('#333333');
  const [width, setWidth] = useState<number>(250);
  const [height, setHeight] = useState<number>(500);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    content: '',
    x: 0,
    y: 0,
  });
  const [activeTab, setActiveTab] = useState<string>('demo');
  const [isMapLoading, setIsMapLoading] = useState<boolean>(true);
  const [animationCompleted, setAnimationCompleted] = useState<boolean>(false);
  const [hoverColor, setHoverColor] = useState<string>('#ffcc00');
  const [strokeWidth, setStrokeWidth] = useState<number>(0.1);

  const tooltipRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Dark mode implementation
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.style.setProperty('--light-bg', '#1a1a2e');
      root.style.setProperty('--card-bg', '#16213e');
      root.style.setProperty('--text-color', '#e6e6e6');
      root.style.setProperty('--text-light', '#b0b0b0');
    } else {
      root.style.setProperty('--light-bg', '#f8f9fa');
      root.style.setProperty('--card-bg', '#ffffff');
      root.style.setProperty('--text-color', '#2b2d42');
      root.style.setProperty('--text-light', '#6c757d');
    }
  }, [isDarkMode]);

  // Simulate map loading with animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMapLoading(false);

      // Add a slight delay before triggering animations
      setTimeout(() => {
        setAnimationCompleted(true);
      }, 300);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Add scroll animation effect
  useEffect(() => {
    const observerOptions: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [activeTab]);

  const handleDistrictHover = (district: string, event: React.MouseEvent) => {
    if (district) {
      setTooltip({
        visible: true,
        content: district.charAt(0).toUpperCase() + district.slice(1),
        x: event.clientX,
        y: event.clientY,
      });
    } else {
      setTooltip({ ...tooltip, visible: false });
    }
  };

  // Update tooltip position when mouse moves
  useEffect(() => {
    if (tooltip.visible && tooltipRef.current) {
      tooltipRef.current.style.left = `${tooltip.x + 10}px`;
      tooltipRef.current.style.top = `${tooltip.y - 30}px`;
    }
  }, [tooltip]);

  // Demo section content
  const renderDemoSection = () => (
    <div className="row">
      <div className="col-lg-8">
        <SectionCard
          title={
            <div className="d-flex justify-content-between align-items-center w-100 flex-wrap">
              <span className="d-flex align-items-center mb-2 mb-md-0">
                <i className="bi bi-map-fill me-2"></i>
                Interactive Map Demo
              </span>
              <span className="badge bg-light text-primary d-flex align-items-center">
                <i className="bi bi-mouse me-1 d-none d-md-block"></i>
                <i className="bi bi-hand-index me-1 d-block d-md-none"></i>
                <span className="d-none d-md-inline">Click</span>
                <span className="d-inline d-md-none">Tap</span> to Interact
              </span>
            </div>
          }
        >
          <div className="map-container-wrapper d-flex flex-column align-items-center justify-content-center">
            <div className="map-container position-relative" ref={mapContainerRef}>
              {isMapLoading && (
                <div className="map-loading">
                  <div className="spinner"></div>
                </div>
              )}
              <div
                className={`text-center ${animationCompleted ? 'animate-fade-in' : 'opacity-0'}`}
              >
                <SriLankaDistrictMap
                  selectedDistrict={selectedDistrict}
                  onDistrictChange={setSelectedDistrict}
                  onDistrictHover={handleDistrictHover}
                  onDistrictLeave={() => setTooltip({ ...tooltip, visible: false })}
                  width={width}
                  height={height}
                  highlightColor={highlightColor}
                  mapbackgroundcolor={defaultColor}
                  strokeColor={strokeColor}
                  strokeWidth={strokeWidth}
                  mapRegionClassName={`${selectedDistrict ? 'selected-district-pulse' : ''}`}
                  hoverColor={hoverColor}
                />
              </div>
            </div>
          </div>
        </SectionCard>
      </div>

      <div className="col-lg-4">
        <div className="sticky-top" style={{ top: '2rem' }}>
          <SectionCard
            title={
              <div className="d-flex align-items-center">
                <i className="bi bi-sliders me-2"></i>
                Customization Options
              </div>
            }
          >
            <div className="controls-panel">
              <div className="mb-4">
                <label htmlFor="district-select" className="form-label d-flex align-items-center">
                  <i className="bi bi-geo-alt me-2 text-primary"></i>
                  Select a district:
                </label>
                <select
                  id="district-select"
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  className="form-select"
                >
                  <option value="">Select a district</option>
                  {districts.map((district) => (
                    <option key={district} value={district}>
                      {district.charAt(0).toUpperCase() + district.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="highlight-color"
                  className="form-label d-flex justify-content-between align-items-center"
                >
                  <span className="d-flex align-items-center">
                    <i className="bi bi-palette me-2 text-primary"></i>
                    Highlight Color:
                  </span>
                  <span
                    className="badge"
                    style={{ backgroundColor: highlightColor, color: 'white' }}
                  >
                    {highlightColor}
                  </span>
                </label>
                <input
                  id="highlight-color"
                  type="color"
                  value={highlightColor}
                  onChange={(e) => setHighlightColor(e.target.value)}
                  className="form-control form-control-color w-100"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="default-color"
                  className="form-label d-flex justify-content-between align-items-center"
                >
                  <span className="d-flex align-items-center">
                    <i className="bi bi-brush me-2 text-primary"></i>
                    Map Background:
                  </span>
                  <span className="badge" style={{ backgroundColor: defaultColor, color: '#333' }}>
                    {defaultColor}
                  </span>
                </label>
                <input
                  id="default-color"
                  type="color"
                  value={defaultColor}
                  onChange={(e) => setDefaultColor(e.target.value)}
                  className="form-control form-control-color w-100"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="hover-color"
                  className="form-label d-flex justify-content-between align-items-center"
                >
                  <span className="d-flex align-items-center">
                    <i className="bi bi-hand-index me-2 text-primary"></i>
                    Hover Color:
                  </span>
                  <span className="badge" style={{ backgroundColor: hoverColor, color: '#333' }}>
                    {hoverColor}
                  </span>
                </label>
                <input
                  id="hover-color"
                  type="color"
                  value={hoverColor}
                  onChange={(e) => setHoverColor(e.target.value)}
                  className="form-control form-control-color w-100"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="stroke-color"
                  className="form-label d-flex justify-content-between align-items-center"
                >
                  <span className="d-flex align-items-center">
                    <i className="bi bi-pencil me-2 text-primary"></i>
                    Stroke Color:
                  </span>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: strokeColor,
                      color: strokeColor === '#ffffff' ? '#333' : 'white',
                    }}
                  >
                    {strokeColor}
                  </span>
                </label>
                <input
                  id="stroke-color"
                  type="color"
                  value={strokeColor}
                  onChange={(e) => setStrokeColor(e.target.value)}
                  className="form-control form-control-color w-100"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="stroke-width"
                  className="form-label d-flex justify-content-between align-items-center"
                >
                  <span className="d-flex align-items-center">
                    <i className="bi bi-rulers me-2 text-primary"></i>
                    Stroke Width:
                  </span>
                  <span className="badge bg-light text-primary">{strokeWidth}</span>
                </label>
                <input
                  id="stroke-width"
                  type="range"
                  min="0.05"
                  max="0.5"
                  step="0.05"
                  value={strokeWidth}
                  onChange={(e) => setStrokeWidth(parseFloat(e.target.value))}
                  className="form-range"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="map-width"
                  className="form-label d-flex justify-content-between align-items-center"
                >
                  <span className="d-flex align-items-center">
                    <i className="bi bi-arrows-angle-expand me-2 text-primary"></i>
                    Map Width:
                  </span>
                  <span className="badge bg-light text-primary">{width}px</span>
                </label>
                <input
                  id="map-width"
                  type="range"
                  min="150"
                  max="500"
                  value={width}
                  onChange={(e) => setWidth(parseInt(e.target.value))}
                  className="form-range"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="map-height"
                  className="form-label d-flex justify-content-between align-items-center"
                >
                  <span className="d-flex align-items-center">
                    <i className="bi bi-arrows-angle-contract me-2 text-primary"></i>
                    Map Height:
                  </span>
                  <span className="badge bg-light text-primary">{height}px</span>
                </label>
                <input
                  id="map-height"
                  type="range"
                  min="300"
                  max="700"
                  value={height}
                  onChange={(e) => setHeight(parseInt(e.target.value))}
                  className="form-range"
                />
              </div>
            </div>

            {selectedDistrict && (
              <div
                className="p-3 rounded-3 district-info mt-3"
                style={{
                  backgroundColor: `${highlightColor}20`,
                  borderLeft: `4px solid ${highlightColor}`,
                }}
              >
                <div className="d-flex align-items-center flex-wrap">
                  <i className="bi bi-geo-alt-fill me-2" style={{ color: highlightColor }}></i>
                  <span className="fw-bold me-2">Selected:</span>
                  <span
                    className="badge"
                    style={{ backgroundColor: highlightColor, color: 'white' }}
                  >
                    {selectedDistrict.charAt(0).toUpperCase() + selectedDistrict.slice(1)}
                  </span>
                </div>
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );

  // Examples section content
  const renderExamplesSection = () => (
    <SectionCard
      title={
        <div className="d-flex align-items-center">
          <i className="bi bi-palette-fill me-2"></i>
          Map Styling Examples
        </div>
      }
    >
      <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 examples-grid">
        {/* Example cards with responsive layout */}
        {/* Classic Style */}
        <div className="col animate-on-scroll d-flex">
          <div className="card w-100 text-center">
            <div className="card-header bg-light">
              <h3 className="h5 mb-0">Classic Style</h3>
            </div>
            <div className="card-body d-flex flex-column align-items-center justify-content-between">
              <SriLankaDistrictMap
                selectedDistrict="colombo"
                width={180}
                height={360}
                interactive={false}
                highlightColor="#4361ee"
                hoverColor="#a3b8ff"
                strokeWidth={0.2}
              />
              <div className="mt-3">
                <span className="badge bg-primary">Default Theme</span>
              </div>
            </div>
          </div>
        </div>

        {/* Nature Theme */}
        <div className="col animate-on-scroll d-flex">
          <div className="card w-100 text-center">
            <div className="card-header bg-light">
              <h3 className="h5 mb-0">Nature Theme</h3>
            </div>
            <div className="card-body d-flex flex-column align-items-center justify-content-between">
              <SriLankaDistrictMap
                selectedDistrict="galle"
                width={180}
                height={360}
                highlightColor="#2a9d8f"
                mapbackgroundcolor="#e9f5db"
                strokeColor="#264653"
                hoverColor="#83c5be"
                strokeWidth={0.15}
                interactive={false}
              />
              <div className="mt-3">
                <span className="badge" style={{ backgroundColor: '#2a9d8f' }}>
                  Eco Friendly
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Design */}
        <div className="col animate-on-scroll d-flex">
          <div className="card w-100 text-center">
            <div className="card-header bg-light">
              <h3 className="h5 mb-0">Modern Design</h3>
            </div>
            <div className="card-body d-flex flex-column align-items-center justify-content-between">
              <SriLankaDistrictMap
                selectedDistrict="kandy"
                width={180}
                height={360}
                highlightColor="#f72585"
                mapbackgroundcolor="#f8f9fa"
                strokeColor="#3a0ca3"
                hoverColor="#ffb3d9"
                strokeWidth={0.25}
                interactive={false}
              />
              <div className="mt-3">
                <span className="badge" style={{ backgroundColor: '#f72585' }}>
                  Contemporary
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );

  // Usage section content with updated documentation
  const renderUsageSection = () => (
    <SectionCard
      title={
        <div className="d-flex align-items-center">
          <i className="bi bi-code-slash me-2"></i>
          Usage Instructions
        </div>
      }
    >
      <div className="alert alert-info mb-4">
        <div className="d-flex">
          <i className="bi bi-info-circle-fill me-2 mt-1"></i>
          <div>
            <strong>Installation:</strong>
            <code className="ms-2">npm install srilanka-map</code> or{' '}
            <code>yarn add srilanka-districts-map</code>
          </div>
        </div>
      </div>

      <div className="mb-4 animate-on-scroll">
        <h4 className="h5 mb-3 d-flex align-items-center">
          <i className="bi bi-1-circle-fill me-2 text-primary"></i>
          Basic Usage:
        </h4>
        <pre className="code-block">
          <code>
            {`import React, { useState } from 'react';
import SriLankaDistrictMap from 'srilanka-districts-map';

function App() {
  const [selectedDistrict, setSelectedDistrict] = useState('');
  
  return (
    <SriLankaDistrictMap 
      selectedDistrict={selectedDistrict}
      onDistrictChange={setSelectedDistrict}
    />
  );
}`}
          </code>
        </pre>
      </div>

      <div className="mb-4 animate-on-scroll">
        <h4 className="h5 mb-3 d-flex align-items-center">
          <i className="bi bi-2-circle-fill me-2 text-primary"></i>
          Advanced Configuration:
        </h4>
        <pre className="code-block">
          <code>
            {`import React, { useState } from 'react';
import SriLankaDistrictMap, { districts } from 'srilanka-districts-map';

function App() {
  const [selectedDistrict, setSelectedDistrict] = useState('colombo');
  
  const handleDistrictHover = (district, event) => {
    console.log('Hovering over:', district);
  }
  
  const handleDistrictLeave = () => {
    console.log('Left district');
  }
  
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <SriLankaDistrictMap 
        selectedDistrict={selectedDistrict}
        onDistrictChange={setSelectedDistrict}
        onDistrictHover={handleDistrictHover}
        onDistrictLeave={handleDistrictLeave}
        width={250}
        height={500}
        highlightColor="#4361ee"
        mapbackgroundcolor="#e8f0fe"
        strokeColor="#333333"
        strokeWidth={0.15}
        hoverColor="#ffcc00"
        mapRegionClassName="custom-map-region"
        interactive={true}
      />
    </div>
  );
}`}
          </code>
        </pre>
      </div>

      <div className="mb-4 animate-on-scroll">
        <h4 className="h5 mb-3 d-flex align-items-center">
          <i className="bi bi-3-circle-fill me-2 text-primary"></i>
          Available Props:
        </h4>
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                <th>Prop</th>
                <th>Type</th>
                <th>Default</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>selectedDistrict</code>
                </td>
                <td>string</td>
                <td>&apos;&apos;</td>
                <td>Currently selected district name</td>
              </tr>
              <tr>
                <td>
                  <code>onDistrictChange</code>
                </td>
                <td>function</td>
                <td>undefined</td>
                <td>Callback when district is clicked</td>
              </tr>
              <tr>
                <td>
                  <code>onDistrictHover</code>
                </td>
                <td>function</td>
                <td>undefined</td>
                <td>Callback when district is hovered</td>
              </tr>
              <tr>
                <td>
                  <code>onDistrictLeave</code>
                </td>
                <td>function</td>
                <td>undefined</td>
                <td>Callback when mouse leaves a district</td>
              </tr>
              <tr>
                <td>
                  <code>width</code>
                </td>
                <td>number</td>
                <td>250</td>
                <td>Width of the map in pixels</td>
              </tr>
              <tr>
                <td>
                  <code>height</code>
                </td>
                <td>number</td>
                <td>500</td>
                <td>Height of the map in pixels</td>
              </tr>
              <tr>
                <td>
                  <code>highlightColor</code>
                </td>
                <td>string</td>
                <td>#4361ee</td>
                <td>Color for selected district</td>
              </tr>
              <tr>
                <td>
                  <code>mapbackgroundcolor</code>
                </td>
                <td>string</td>
                <td>#e8f0fe</td>
                <td>Default color for districts</td>
              </tr>
              <tr>
                <td>
                  <code>hoverColor</code>
                </td>
                <td>string</td>
                <td>#ffcc00</td>
                <td>Color for district on hover</td>
              </tr>
              <tr>
                <td>
                  <code>strokeColor</code>
                </td>
                <td>string</td>
                <td>#333333</td>
                <td>Color for district borders</td>
              </tr>
              <tr>
                <td>
                  <code>strokeWidth</code>
                </td>
                <td>number</td>
                <td>0.1</td>
                <td>Width of district borders</td>
              </tr>
              <tr>
                <td>
                  <code>interactive</code>
                </td>
                <td>boolean</td>
                <td>true</td>
                <td>Whether the map responds to mouse events</td>
              </tr>
              <tr>
                <td>
                  <code>mapRegionClassName</code>
                </td>
                <td>string</td>
                <td>&apos;&apos;</td>
                <td>Additional CSS class for map regions</td>
              </tr>
              <tr>
                <td>
                  <code>className</code>
                </td>
                <td>string</td>
                <td>&apos;&apos;</td>
                <td>Additional CSS class for the SVG element</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-4 animate-on-scroll">
        <h4 className="h5 mb-3 d-flex align-items-center">
          <i className="bi bi-info-circle-fill me-2 text-primary"></i>
          Available Districts:
        </h4>
        <pre className="code-block">
          <code>
            {`import { districts } from 'srilanka-districts-map';

console.log(districts);
// [
//   'ampara', 'anuradhapura', 'badulla', 'batticaloa', 'colombo', 
//   'galle', 'gampaha', 'hambantota', 'jaffna', 'kalutara', 
//   'kandy', 'kegalle', 'kilinochchi', 'kurunegala', 'mannar', 
//   'matale', 'matara', 'monaragala', 'mullaitivu', 'nuwara eliya', 
//   'polonnaruwa', 'puttalam', 'ratnapura', 'trincomalee', 'vavuniya'
// ]`}
          </code>
        </pre>
      </div>
    </SectionCard>
  );

  return (
    <div
      className={`container-fluid py-4 py-md-5 px-2 px-md-4 ${
        isDarkMode ? 'bg-dark text-light' : 'bg-light'
      }`}
    >
      {/* Header Section */}
      <div className="row justify-content-center mb-4 mb-md-5">
        <div className="col-12 col-md-10 col-lg-8 text-center">
          <div className="demo-header">
            <h1 className="display-4 mb-2 mb-md-3">
              <i className="bi bi-globe-asia-australia me-2"></i> Sri Lanka District Map
            </h1>
            <p className="lead px-1">
              An interactive, customizable React component for visualizing Sri Lankan districts
            </p>

            {/* Badges */}
            <div className="d-flex justify-content-center gap-2 mt-3 mt-md-4 flex-wrap">
              <span className="badge bg-primary d-flex align-items-center">
                <i className="bi bi-lightning-charge-fill me-1"></i> Interactive
              </span>
              <span className="badge bg-success d-flex align-items-center">
                <i className="bi bi-palette-fill me-1"></i> Customizable
              </span>
              <span className="badge bg-info d-flex align-items-center">
                <i className="bi bi-aspect-ratio-fill me-1"></i> Responsive
              </span>
            </div>
          </div>

          {/* Tab navigation - mobile friendly */}
          <div className="nav-tab-container">
            <div className="d-flex flex-column flex-sm-row w-100">
              <button
                className={`btn tab-btn ${
                  activeTab === 'demo' ? 'active' : 'btn-outline-primary'
                } mx-1 my-1 d-flex align-items-center justify-content-center`}
                onClick={() => setActiveTab('demo')}
              >
                <i className="bi bi-map-fill me-2"></i> Demo
              </button>
              <button
                className={`btn tab-btn ${
                  activeTab === 'examples' ? 'active' : 'btn-outline-primary'
                } mx-1 my-1 d-flex align-items-center justify-content-center`}
                onClick={() => setActiveTab('examples')}
              >
                <i className="bi bi-palette-fill me-2"></i> Examples
              </button>
              <button
                className={`btn tab-btn ${
                  activeTab === 'usage' ? 'active' : 'btn-outline-primary'
                } mx-1 my-1 d-flex align-items-center justify-content-center`}
                onClick={() => setActiveTab('usage')}
              >
                <i className="bi bi-code-slash me-2"></i> Usage
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Render appropriate section based on active tab */}
      {activeTab === 'demo' && renderDemoSection()}
      {activeTab === 'examples' && renderExamplesSection()}
      {activeTab === 'usage' && renderUsageSection()}

      {/* Dark Mode Toggle */}
      <button
        className="mode-toggle"
        onClick={() => setIsDarkMode(!isDarkMode)}
        aria-label="Toggle dark mode"
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? (
          <i className="bi bi-sun-fill" style={{ fontSize: '20px' }}></i>
        ) : (
          <i className="bi bi-moon-fill" style={{ fontSize: '20px' }}></i>
        )}
      </button>

      {/* Tooltip */}
      {tooltip.visible && (
        <div ref={tooltipRef} className="district-tooltip visible">
          {tooltip.content}
        </div>
      )}
    </div>
  );
}

export default App;
